import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { questions } from '../data/questions';
import { saveBusinessQuizLead } from '../logic/api';
import ProgressBar from './ProgressBar';
import QuestionRenderer from './QuestionRenderer';
import LeadForm from './LeadForm';
import IntroStep from './IntroStep';
import UnderinsuredResult from './results/UnderinsuredResult';
import OverpayingResult from './results/OverpayingResult';
import NeedReviewResult from './results/NeedReviewResult';

export default function QuizFunnel() {
  // Use -1 for the Intro/Start step
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultType, setResultType] = useState(null);

  // Load saved progress
  useEffect(() => {
    const saved = localStorage.getItem('bizQuizProgress');
    if (saved) {
      const { savedStep, savedAnswers } = JSON.parse(saved);
      // Only restore if they were actually in the quiz (step >= 0)
      if (savedStep >= 0 && savedStep < questions.length) {
        setStep(savedStep);
        setAnswers(savedAnswers);
      }
    }
  }, []);

  // Save progress automatically
  useEffect(() => {
    if (step >= 0 && step < questions.length) {
      localStorage.setItem('bizQuizProgress', JSON.stringify({
        savedStep: step,
        savedAnswers: answers
      }));
    }
  }, [step, answers]);

  const handleStart = () => {
    setStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAnswer = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [questions[step].id]: answer
    }));
  };

  const handleNext = () => {
    if (step < questions.length) {
      setStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (step > -1) {
      setStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const response = await saveBusinessQuizLead(formData, answers);
      setResultType(response.resultType);
      localStorage.removeItem('bizQuizProgress');
      setStep(questions.length + 1); // Move to results step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error("Submission error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-4xl mx-auto">
        
        {/* Progress Bar (Only visible during quiz) */}
        {step >= 0 && step < questions.length && (
          <>
            <div className="flex justify-center mb-8">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/525d05658fdf988acaa34e738d3f286826191483600217441074af5254e1324b9947d16ec384fcc03e2360d22f6a7d9b/images/aW1hZ2UvcG5n/312034cd79c028aad1e85c1e994b94aa.png" 
                alt="Pinoy General Insurance Services" 
                className="w-40 md:w-56 h-auto object-contain"
              />
            </div>
            <ProgressBar currentStep={step} totalSteps={questions.length} />
          </>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {/* Intro Step */}
            {step === -1 && (
              <IntroStep onStart={handleStart} />
            )}

            {/* Quiz Questions */}
            {step >= 0 && step < questions.length && (
              <QuestionRenderer
                question={questions[step]}
                answer={answers[questions[step].id]}
                onAnswer={handleAnswer}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}

            {/* Lead Capture Form */}
            {step === questions.length && (
              <LeadForm 
                onSubmit={handleFormSubmit} 
                isSubmitting={isSubmitting} 
              />
            )}

            {/* Result Pages */}
            {step > questions.length && resultType === 'underinsured' && <UnderinsuredResult answers={answers} />}
            {step > questions.length && resultType === 'overpaying' && <OverpayingResult answers={answers} />}
            {step > questions.length && resultType === 'need_review' && <NeedReviewResult answers={answers} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}